import React, { useState, ReactElement, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Menu, MenuItem, Toolbar, AppBar, Box, Button, Typography, IconButton, Link } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { signInWithPopup, signOut, getAuth, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';

import UserContext from '../../store/user-context';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ColorModeContext from '../../store/color-context';

import appConfig from '../../config/app.config';

const { run, streamerDisplayText: runner } = appConfig; 

type Page = {
  text: string;
  url?: string;
  action?: string;
  requireAuth?: boolean;
  showUserName?: boolean;
  hiddenWhenAuth?: boolean;
};

const pages: Page[] = [
  {
    text: 'Timeline',
    url: '/',
  },
  {
    text: 'Statistics',
    url: '/statistics',
  },
  {
    text: 'Time Progress',
    url: '/visual',
  },
  {
    text: 'Add new Runs',
    url: '/form',
    requireAuth: true
  },
  {
    text: 'Edit Runs',
    url: '/edit',
    requireAuth: true
  },
  {
    text: 'Login',
    action: 'login',
    hiddenWhenAuth: true
  },
  {
    text: 'Logout',
    action: 'logout',
    showUserName: true,
    requireAuth: true
  }
];

const MenuComponent = () : ReactElement => {

  
  const history = useHistory();
  const userContext = useContext(UserContext);
  const auth = getAuth(userContext.firebaseApp);
  const provider = new GoogleAuthProvider();
  const colorMode = React.useContext(ColorModeContext);

  const [anchorNav, setAnchorNav] = useState<null | HTMLElement>(
    null
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorNav(null);
  };

  onAuthStateChanged(auth, (user) => {
    if(!!userContext && !!userContext.setUser){
      if(user) {
        userContext.setUser(user);
        return;
      }
    }
    userContext.setUser?.(undefined);
  });

  const handleMenuItemClick = (page: Page) => {
    setAnchorNav(null);
    if(page.action) {
      switch(page.action) {
        case 'login':
          signInWithPopup(auth, provider);
          break;
        case 'logout':
          signOut(auth);
          history.push("/");
          break;
        default:
          break;
      }
    } else {
      history.push(page?.url || "");
    }
  }

  return (
    <AppBar position="static" color="primary">
        <Toolbar disableGutters>
          <IconButton sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            {runner}'s {run}
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }}}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' }
              }}
            >
              {pages.map((page: Page, index: number) => {
                if(page.requireAuth && !userContext.user) {
                  return undefined;
                }
                if(page.hiddenWhenAuth && userContext.user) {
                  return undefined;
                }
                return (
                  <MenuItem key={index} onClick={() => handleMenuItemClick(page)}>
                    <Typography textAlign="center">{page.text}</Typography>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
          <IconButton sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />
          <Typography
            variant="h5"
            noWrap
            component="a"
            href=""
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            {runner}'s {run}
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page: Page, index: number) => {
              let text = page.text;
              if(page.requireAuth && !userContext.user) {
                return undefined;
              }
              if(page.hiddenWhenAuth && userContext.user) {
                return undefined;
              }
              if(page.showUserName) {
                text = `${page.text} (${userContext?.user?.email})`;
              }
              return (
                page.url ?
                  <Link key={index} href={page.url} sx={{ my: 2, display: 'block', fontSize: '1.3em', lineHeight: '1.9em', padding: '0 1em', color: 'inherit', textDecoration: 'none', text, textTransform: 'uppercase', verticalAlign: 'middle' }} >
                  {text}
                  </Link>
                  :
                  <Button
                  variant={page.hiddenWhenAuth || page.showUserName ? 'outlined' : 'text'}
                  color="inherit"
                  onClick={() => handleMenuItemClick(page)}
                  key={index}
                  sx={{ my: 2, display: 'block', fontSize: '1.3em', padding: '0 1em' }}
                  >
                  {text}
                  </Button>
              )
            }
          )}
          </Box>
          <IconButton sx={{ ml: 1 }} onClick={colorMode.toggleColorMode} color="inherit">
          {colorMode.colorMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        </Toolbar>
    </AppBar>
  );
};

export default MenuComponent;